// Profile.tsx
import * as React from "react";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Button,
  Stack,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Switch,
  Skeleton,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ContactPageOutlinedIcon from "@mui/icons-material/ContactPageOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import {
  createSignedStorageUrl,
  deleteAuthAccount,
  deleteMyAccountData,
  exportUserData as exportUserDataQuery,
  fetchCompanyById,
  fetchLatestAssessmentTimestamp,
  fetchProfileEmployeeByAuthUserId,
  getCurrentSession,
  removeAvatar as removeAvatarQuery,
  sendProfileQuestion,
  setMyAnonymity,
  signOut,
  updateMyProfileDetails,
  uploadAvatar as uploadAvatarQuery,
} from "../api/queries";
import { useI18n } from "../i18n/I18nProvider";
import { brand } from "../theme";
import type { EmployeeRow } from "../types/profile";
import type { CompanyRow } from "../types/company";


function initials(first?: string | null, last?: string | null) {
  const f = (first ?? "").trim();
  const l = (last ?? "").trim();
  return `${f ? f[0].toUpperCase() : ""}${l ? l[0].toUpperCase() : ""}` || "U";
}

function safeName(first?: string | null, last?: string | null, anonymous?: boolean) {
  if (anonymous) return "Anonymous User";
  const full = `${(first ?? "").trim()} ${(last ?? "").trim()}`.trim();
  return full || "User";
}

function isEmployeeRow(x: unknown): x is EmployeeRow {
  return !!x && typeof x === "object" && typeof (x as any).id === "string";
}

function formatAssessmentDate(ts: string | null, lang: "en" | "de") {
  if (!ts) return "—";

  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";

  const monthsEn = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthsDe = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
  const months = lang === "de" ? monthsDe : monthsEn;

  const yyyy = d.getFullYear();
  const mmm = months[d.getMonth()];
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");

  return `${yyyy} ${mmm} ${dd} - ${hh}:${mm}`;
}

const PROFILE_SECTION_IDS = [
  "overview",
  "details",
  "privacy",
  "account",
  "support",
] as const;

type ProfileSectionId = (typeof PROFILE_SECTION_IDS)[number];

const btnPrimarySx = {
  textTransform: "none",
  borderRadius: 2.5,
  bgcolor: brand.ink,
  boxShadow: "none",
  fontWeight: 900,
  px: 2,
  py: 1.1,
  "&:hover": { bgcolor: brand.inkDeep, boxShadow: "none" },
} as const;

const btnOutlineSx = {
  textTransform: "none",
  borderRadius: 2.5,
  fontWeight: 800,
  borderColor: brand.borderStrong,
  color: brand.textPrimary,
  bgcolor: brand.surface,
  "&:hover": { borderColor: brand.textFaint, bgcolor: brand.surfaceMuted },
} as const;

export default function ProfilePage(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;

  const { lang } = useI18n();
  const typedLang: "en" | "de" = lang === "de" ? "de" : "en";
  const L = React.useCallback((en: string, de: string) => (lang === "de" ? de : en), [lang]);

  const [employee, setEmployee] = React.useState<EmployeeRow | null>(null);
  const [company, setCompany] = React.useState<CompanyRow | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const [lastAssessmentAt, setLastAssessmentAt] = React.useState<string | null>(null);

  const [anonDialogOpen, setAnonDialogOpen] = React.useState(false);
  const [identityDialogOpen, setIdentityDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);

  const [saving, setSaving] = React.useState(false);

  const [identityForm, setIdentityForm] = React.useState({
    first_name: "",
    last_name: "",
    position: "",
    department: "",
    personal_address: "",
  });

  const hasIdentity =
    Boolean(employee?.first_name?.trim()) &&
    Boolean(employee?.last_name?.trim()) &&
    Boolean(employee?.position?.trim()) &&
    Boolean(employee?.personal_address?.trim());

  const useAsAnonymous = employee?.is_anonymous ?? !hasIdentity;
  const emailIsHidden = useAsAnonymous;
  const addressIsHidden = useAsAnonymous;

  const [avatarSignedUrl, setAvatarSignedUrl] = React.useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const [questionText, setQuestionText] = React.useState("");
  const [sendingQuestion, setSendingQuestion] = React.useState(false);
  const [questionSuccess, setQuestionSuccess] = React.useState<string | null>(null);
  const [questionError, setQuestionError] = React.useState<string | null>(null);

  const refreshAvatarSignedUrl = React.useCallback(async (path: string | null | undefined) => {
    if (!path) {
      setAvatarSignedUrl(null);
      return;
    }

    const { data, error } = await createSignedStorageUrl("avatars", path, 60 * 60);

    if (error) {
      setAvatarSignedUrl(null);
      return;
    }

    setAvatarSignedUrl(data.signedUrl);
  }, []);

  const syncIdentityForm = React.useCallback((emp: EmployeeRow | null) => {
    setIdentityForm({
      first_name: emp?.first_name ?? "",
      last_name: emp?.last_name ?? "",
      position: emp?.position ?? "",
      department: emp?.department ?? "",
      personal_address: emp?.personal_address ?? "",
    });
  }, []);

  const loadProfile = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const { data: sessionData, error: sessionErr } = await getCurrentSession();
      if (sessionErr) throw sessionErr;

      const userId = sessionData.session?.user?.id;

      if (!userId) {
        setEmployee(null);
        setCompany(null);
        setLastAssessmentAt(null);
        setLoadError(L("Not signed in.", "Nicht angemeldet."));
        return;
      }

      const { data: emp, error: empErr } = await fetchProfileEmployeeByAuthUserId(userId);

      if (empErr) throw empErr;

      const empRow = isEmployeeRow(emp) ? emp : null;

      setEmployee(empRow);
      syncIdentityForm(empRow);

      if (empRow?.avatar_url) await refreshAvatarSignedUrl(empRow.avatar_url);
      else setAvatarSignedUrl(null);

      if (empRow?.id) {
        const { data: latestScoreRow, error: latestScoreErr } =
          await fetchLatestAssessmentTimestamp(empRow.id);

        if (latestScoreErr) {
          console.warn("Could not load last assessment date:", latestScoreErr.message);
          setLastAssessmentAt(null);
        } else {
          setLastAssessmentAt(latestScoreRow?.updated_at ?? null);
        }
      } else {
        setLastAssessmentAt(null);
      }

      if (!empRow?.company_id) {
        setCompany(null);
        return;
      }

      const { data: comp, error: compErr } = await fetchCompanyById(empRow.company_id);

      if (compErr) {
        setCompany(null);
        return;
      }

      setCompany((comp as CompanyRow) ?? null);
    } catch (e: any) {
      setEmployee(null);
      setCompany(null);
      setLastAssessmentAt(null);
      setLoadError(e?.message ?? L("Failed to load profile.", "Profil konnte nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  }, [L, refreshAvatarSignedUrl, syncIdentityForm]);

  React.useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const uploadAvatar = React.useCallback(
    async (file: File) => {
      setLoadError(null);
      setUploadingAvatar(true);

      try {
        const { data: sessionData, error: sessionErr } = await getCurrentSession();
        if (sessionErr) throw sessionErr;

        const token = sessionData.session?.access_token;
        if (!token) throw new Error(L("Not authenticated.", "Nicht authentifiziert."));

        const { data, error } = await uploadAvatarQuery(file, token);

        if (error) throw new Error(error.message || L("Failed to upload avatar.", "Avatar konnte nicht hochgeladen werden."));
        if (!data?.ok) throw new Error(data?.reason || L("Failed to upload avatar.", "Avatar konnte nicht hochgeladen werden."));

        const nextPath = data.avatar_url ?? null;
        const nextSigned = data.signed_url ?? null;

        setEmployee((prev) => (prev ? { ...prev, avatar_url: nextPath } : prev));
        setAvatarSignedUrl(nextSigned);

        if (!nextSigned && nextPath) await refreshAvatarSignedUrl(nextPath);
      } catch (e: any) {
        setLoadError(e?.message ?? L("Avatar upload failed.", "Avatar-Upload fehlgeschlagen."));
      } finally {
        setUploadingAvatar(false);
      }
    },
    [L, refreshAvatarSignedUrl]
  );

  const removeAvatar = React.useCallback(async () => {
    setLoadError(null);
    setUploadingAvatar(true);

    try {
      const { data: sessionData, error: sessionErr } = await getCurrentSession();
      if (sessionErr) throw sessionErr;

      const token = sessionData.session?.access_token;
      if (!token) throw new Error(L("Not authenticated.", "Nicht authentifiziert."));

      const { data, error } = await removeAvatarQuery(token);

      if (error) throw new Error(error.message || L("Failed to remove avatar.", "Avatar konnte nicht entfernt werden."));
      if (!data?.ok) throw new Error(data?.reason || L("Failed to remove avatar.", "Avatar konnte nicht entfernt werden."));

      setEmployee((prev) => (prev ? { ...prev, avatar_url: null } : prev));
      setAvatarSignedUrl(null);
    } catch (e: any) {
      setLoadError(e?.message ?? L("Avatar remove failed.", "Avatar konnte nicht entfernt werden."));
    } finally {
      setUploadingAvatar(false);
    }
  }, [L]);

  async function enableAnonymousMode() {
    setSaving(true);
    setLoadError(null);

    const { error } = await setMyAnonymity(true);

    setSaving(false);

    if (error) {
      setLoadError(error.message);
      return;
    }

    const nextEmployee = employee
      ? {
          ...employee,
          is_anonymous: true,
          first_name: null,
          last_name: null,
          position: null,
          personal_address: null,
          location: null,
          anonymity_updated_at: new Date().toISOString(),
        }
      : null;

    setEmployee(nextEmployee);
    syncIdentityForm(nextEmployee);
    setAnonDialogOpen(false);
  }

  async function saveProfileDetails() {
    setSaving(true);
    setLoadError(null);

    const { error } = await updateMyProfileDetails({
      firstName: identityForm.first_name,
      lastName: identityForm.last_name,
      position: identityForm.position,
      department: identityForm.department,
      personalAddress: identityForm.personal_address,
    });

    setSaving(false);

    if (error) {
      setLoadError(error.message);
      return;
    }

    setEmployee((prev) =>
      prev
        ? {
            ...prev,
            is_anonymous: false,
            first_name: identityForm.first_name.trim() || null,
            last_name: identityForm.last_name.trim() || null,
            position: identityForm.position.trim() || null,
            department: identityForm.department.trim() || null,
            personal_address: identityForm.personal_address.trim() || null,
            location: identityForm.personal_address.trim() || null,
            anonymity_updated_at: new Date().toISOString(),
          }
        : prev
    );

    setEditDialogOpen(false);
    setIdentityDialogOpen(false);
  }

  function handleAnonymousToggle(next: boolean) {
    if (next === useAsAnonymous) return;

    if (next) {
      setAnonDialogOpen(true);
      return;
    }

    syncIdentityForm(employee);
    setIdentityDialogOpen(true);
  }

  function openEditDialog() {
    syncIdentityForm(employee);
    setEditDialogOpen(true);
  }

  const identityFormValid =
    identityForm.first_name.trim().length > 0 &&
    identityForm.last_name.trim().length > 0 &&
    identityForm.position.trim().length > 0 &&
    identityForm.personal_address.trim().length > 0;

  const sectionRefs = React.useRef<Record<ProfileSectionId, HTMLElement | null>>({
    overview: null,
    details: null,
    privacy: null,
    account: null,
    support: null,
  });

  const [activeSection, setActiveSection] = React.useState<ProfileSectionId>("overview");

  const registerSection = React.useCallback(
    (id: ProfileSectionId) => (el: HTMLElement | null) => {
      sectionRefs.current[id] = el;
    },
    []
  );

  const goToSection = React.useCallback((id: ProfileSectionId) => {
    setActiveSection(id);
    // Guarded: jsdom and older browsers do not implement smooth scrolling.
    sectionRefs.current[id]?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, []);

  // Keeps the side navigation in step with whatever has been scrolled to.
  React.useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const topMost = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];

        const id = (topMost?.target as HTMLElement | undefined)?.dataset?.section;
        if (id) setActiveSection(id as ProfileSectionId);
      },
      { rootMargin: "-96px 0px -55% 0px" }
    );

    for (const id of PROFILE_SECTION_IDS) {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [loading]);

  const displayName = safeName(employee?.first_name, employee?.last_name, useAsAnonymous);
  const displayTitle = useAsAnonymous ? L("Anonymous mode", "Anonymer Modus") : (employee?.position ?? "").trim() || "—";
  const displayDepartment = (employee?.department ?? "").trim() || "—";
  const displayCompanyName = (company?.name ?? "").trim() || "—";
  const lastAssessment = formatAssessmentDate(lastAssessmentAt, typedLang);

  const canSendQuestion = !!employee?.id && questionText.trim().length > 0 && !loading && !sendingQuestion;

  const sendQuestion = async () => {
    setQuestionSuccess(null);
    setQuestionError(null);

    const msg = questionText.trim();

    if (!employee?.id) {
      setQuestionError(L("Employee not loaded. Please reload the page.", "Mitarbeiterdaten nicht geladen. Bitte Seite neu laden."));
      return;
    }

    if (!msg) {
      setQuestionError(L("Please type a question first.", "Bitte zuerst eine Frage eingeben."));
      return;
    }

    try {
      setSendingQuestion(true);

      const { data, error } = await sendProfileQuestion(employee.id, msg);

      if (error) throw error;
      if (!data?.ok) throw new Error(data?.reason || L("Failed to send message.", "Nachricht konnte nicht gesendet werden."));

      setQuestionSuccess(
        L(
          "Message sent to the takeKONTROL team. We'll get back to you.",
          "Nachricht an das takeKONTROL-Team gesendet. Wir melden uns bei dir."
        )
      );
      setQuestionText("");
    } catch (e: any) {
      setQuestionError(e?.message ?? L("Failed to send message. Please try again.", "Nachricht konnte nicht gesendet werden. Bitte erneut versuchen."));
    } finally {
      setSendingQuestion(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    localStorage.removeItem("tk.currentPage");
    localStorage.removeItem("tk.nav.role");
    handlePageChange("Entry");
  };

  const exportUserData = async () => {
    try {
      const { data: sessionData } = await getCurrentSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        alert(L("Not authenticated", "Nicht angemeldet"));
        return;
      }

      const response = await exportUserDataQuery(token);

      if (!response.ok) throw new Error("Export failed");

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "takekontrol_resilience_platform_data.json";

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match?.[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert(L("Export failed", "Export fehlgeschlagen"));
    }
  };

  const deleteAccount = async () => {
    try {
      const {
        data: { session },
      } = await getCurrentSession();

      const token = session?.access_token;

      if (!token) {
        alert("Session expired");
        return;
      }

      const { error: rpcError } = await deleteMyAccountData();

      if (rpcError) {
        console.error("Public cleanup failed:", rpcError);
        alert("Delete failed");
        return;
      }

      const res = await deleteAuthAccount(token);

      if (!res.ok) {
        const text = await res.text();
        console.error("Auth delete failed:", text);
        alert("Auth delete failed");
        return;
      }

      await signOut();
      localStorage.clear();
      sessionStorage.clear();

      handlePageChange("WelcomeScreen");
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Delete failed");
    }
  };

  const navItems = [
    { id: "overview" as const, label: L("Overview", "Übersicht"), icon: <BadgeOutlinedIcon fontSize="small" /> },
    { id: "details" as const, label: L("Personal details", "Persönliche Daten"), icon: <ContactPageOutlinedIcon fontSize="small" /> },
    { id: "privacy" as const, label: L("Privacy", "Datenschutz"), icon: <ShieldOutlinedIcon fontSize="small" /> },
    { id: "account" as const, label: L("Account", "Konto"), icon: <SettingsOutlinedIcon fontSize="small" /> },
    { id: "support" as const, label: L("Support", "Support"), icon: <HelpOutlineOutlinedIcon fontSize="small" /> },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: brand.page, p: { xs: 2, md: 3 } }}>
      <Box sx={{ width: "100%", maxWidth: 1240, mx: "auto" }}>
        <Box sx={{ mb: { xs: 2, md: 2.5 } }}>
          <Typography variant="h5" sx={{ fontWeight: 900, color: brand.textPrimary }}>
            {L("Profile", "Profil")}
          </Typography>
          <Typography variant="body2" sx={{ color: brand.textMuted }}>
            {L(
              "Manage your identity, privacy and account settings.",
              "Verwalte Identität, Datenschutz und Kontoeinstellungen."
            )}
          </Typography>
        </Box>

        {loadError ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
            {loadError}
          </Alert>
        ) : null}

        <Box
          sx={{
            display: { xs: "block", md: "grid" },
            gridTemplateColumns: { md: "232px minmax(0, 1fr)" },
            alignItems: "start",
            gap: { md: 2.5 },
          }}
        >
          <ProfileNav
            items={navItems}
            active={activeSection}
            onSelect={goToSection}
            modeCaption={L("Current mode", "Aktueller Modus")}
            modeLabel={useAsAnonymous ? L("Anonymous", "Anonym") : L("Identified", "Identifiziert")}
          />

          <Stack spacing={{ xs: 1.5, md: 2.5 }} sx={{ minWidth: 0 }}>
            {/* ---------------- overview ---------------- */}
            <ProfileSection
              id="overview"
              innerRef={registerSection("overview")}
              icon={<BadgeOutlinedIcon fontSize="small" />}
              title={L("Overview", "Übersicht")}
              subtitle={L("", "")}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  sx={{ minWidth: 0 }}
                >
                  {loading ? (
                    <Skeleton variant="circular" width={88} height={88} sx={{ flexShrink: 0 }} />
                  ) : (
                    <Box sx={{ position: "relative", width: 88, height: 88, flexShrink: 0 }}>
                      <Avatar
                        src={useAsAnonymous ? undefined : avatarSignedUrl ?? undefined}
                        alt={displayName}
                        imgProps={{ referrerPolicy: "no-referrer" }}
                        sx={{
                          width: 88,
                          height: 88,
                          bgcolor: brand.border,
                          color: brand.textPrimary,
                          border: `1px solid ${brand.border}`,
                          fontSize: 30,
                          fontWeight: 900,
                        }}
                      >
                        {useAsAnonymous ? "A" : initials(employee?.first_name, employee?.last_name)}
                      </Avatar>

                      {uploadingAvatar && (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "50%",
                            bgcolor: "rgba(255,255,255,0.65)",
                            display: "grid",
                            placeItems: "center",
                          }}
                        >
                          <CircularProgress size={28} />
                        </Box>
                      )}

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        hidden
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          if (f) void uploadAvatar(f);
                        }}
                      />
                    </Box>
                  )}

                  <Box sx={{ minWidth: 0 }}>
                    {loading ? (
                      <>
                        <Skeleton variant="rounded" height={24} width={210} />
                        <Skeleton variant="rounded" height={16} width={180} sx={{ mt: 1 }} />
                        <Skeleton variant="rounded" height={16} width={220} sx={{ mt: 1 }} />
                      </>
                    ) : (
                      <>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 900,
                            color: brand.textPrimary,
                            lineHeight: 1.2,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {displayName}
                        </Typography>
                        <Typography variant="body2" sx={{ color: brand.textSecondary, mt: 0.25 }}>
                          {displayTitle}
                        </Typography>
                        <Typography variant="body2" sx={{ color: brand.textMuted }}>
                          {displayCompanyName}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Stack>

                <Stack
                  spacing={1}
                  alignItems={{ xs: "stretch", sm: "flex-end" }}
                  sx={{ width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}
                >
                  <Button
                    variant="contained"
                    startIcon={<EditOutlinedIcon />}
                    onClick={openEditDialog}
                    disabled={loading || useAsAnonymous}
                    sx={btnPrimarySx}
                  >
                    {L("Edit profile", "Profil bearbeiten")}
                  </Button>

                  <Stack direction="row" spacing={1} justifyContent={{ xs: "stretch", sm: "flex-end" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={loading || uploadingAvatar || useAsAnonymous}
                      sx={{ ...btnOutlineSx, flex: { xs: 1, sm: "0 0 auto" } }}
                    >
                      {L("Change photo", "Foto ändern")}
                    </Button>

                    <Button
                      size="small"
                      variant="text"
                      disabled={!employee?.avatar_url || uploadingAvatar || useAsAnonymous}
                      onClick={() => void removeAvatar()}
                      sx={{ textTransform: "none", fontWeight: 800, color: brand.textMuted }}
                    >
                      {L("Remove", "Entfernen")}
                    </Button>
                  </Stack>
                </Stack>
              </Stack>

              {useAsAnonymous ? (
                <Typography variant="body2" sx={{ color: brand.textMuted, mt: 2, lineHeight: 1.55 }}>
                  {L(
                    "Anonymous mode hides your name, photo and contact details. Turn it off under Privacy to edit them again.",
                    "Der anonyme Modus verbirgt Name, Foto und Kontaktdaten. Schalte ihn unter Datenschutz aus, um sie wieder zu bearbeiten."
                  )}
                </Typography>
              ) : null}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  gap: 1.25,
                  mt: 2,
                }}
              >
                <FactTile
                  label={L("Last assessment", "Letztes Assessment")}
                  value={loading ? L("Loading…", "Lädt…") : lastAssessment}
                />
                <FactTile
                  label={L("Department", "Abteilung")}
                  value={displayDepartment}
                />
              </Box>
            </ProfileSection>

            {/* ---------------- personal details ---------------- */}
            <ProfileSection
              id="details"
              innerRef={registerSection("details")}
              icon={<ContactPageOutlinedIcon fontSize="small" />}
              title={L("Personal details", "Persönliche Daten")}
              subtitle={L(
                "Contact and role information stored for you.",
                "Kontakt- und Rollendaten, die für dich gespeichert sind."
              )}
              action={
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditOutlinedIcon />}
                  onClick={openEditDialog}
                  disabled={loading || useAsAnonymous}
                  sx={btnOutlineSx}
                >
                  {L("Edit", "Bearbeiten")}
                </Button>
              }
              disablePadding
            >
              <Box>
                <DetailRow
                  icon={<EmailOutlinedIcon fontSize="small" />}
                  label={L("Company Email", "Firmen-E-Mail")}
                  value={
                    emailIsHidden
                      ? L("Hidden", "Ausgeblendet")
                      : employee?.company_email ?? "—"
                  }
                  muted={emailIsHidden}
                />
                <Divider />
                <DetailRow
                  icon={<LocationOnOutlinedIcon fontSize="small" />}
                  label={L("Personal Address", "Privatadresse")}
                  value={
                    addressIsHidden
                      ? L("Hidden", "Ausgeblendet")
                      : employee?.personal_address ?? "—"
                  }
                  muted={addressIsHidden}
                />
                <Divider />
                <DetailRow
                  icon={<ApartmentOutlinedIcon fontSize="small" />}
                  label={L("Position", "Position")}
                  value={displayTitle}
                />
                <Divider />
                <DetailRow
                  icon={<PlaceOutlinedIcon fontSize="small" />}
                  label={L("Location", "Standort")}
                  value={(employee?.location ?? "").trim() || "—"}
                />
              </Box>
            </ProfileSection>

            {/* ---------------- privacy ---------------- */}
            <ProfileSection
              id="privacy"
              innerRef={registerSection("privacy")}
              icon={<ShieldOutlinedIcon fontSize="small" />}
              title={L("Privacy", "Datenschutz")}
              subtitle={L(
                "Decide how visible you are to your organisation.",
                "Bestimme, wie sichtbar du für deine Organisation bist."
              )}
            >
              <Box
                sx={{
                  borderRadius: 2.5,
                  border: `1px solid ${brand.border}`,
                  bgcolor: brand.surfaceMuted,
                  p: 1.75,
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  spacing={{ xs: 1.25, sm: 2 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900, color: brand.textPrimary }}>
                      {L("Use platform anonymously", "Plattform anonym nutzen")}
                    </Typography>
                    <Typography variant="body2" sx={{ color: brand.textMuted, mt: 0.25, lineHeight: 1.55 }}>
                      {useAsAnonymous
                        ? L(
                            "Anonymous mode is active. Identifying fields are removed.",
                            "Anonymer Modus ist aktiv. Identifizierende Felder wurden entfernt."
                          )
                        : L(
                            "Identifiable mode is active. Switching this on removes identifying fields.",
                            "Identifizierbarer Modus ist aktiv. Beim Einschalten werden identifizierende Felder entfernt."
                          )}
                    </Typography>
                  </Box>

                  <Switch
                    checked={useAsAnonymous}
                    onChange={(e) => handleAnonymousToggle(e.target.checked)}
                    sx={{ flexShrink: 0 }}
                  />
                </Stack>
              </Box>

              <Typography variant="body2" sx={{ color: brand.textMuted, mt: 1.5, lineHeight: 1.55 }}>
                {L(
                  "Your assessment answers and scores are kept either way — only the details that identify you change.",
                  "Deine Assessment-Antworten und Scores bleiben in beiden Fällen erhalten — nur die identifizierenden Angaben ändern sich."
                )}
              </Typography>
            </ProfileSection>

            {/* ---------------- account ---------------- */}
            <ProfileSection
              id="account"
              innerRef={registerSection("account")}
              icon={<SettingsOutlinedIcon fontSize="small" />}
              title={L("Account", "Konto")}
              subtitle={L(
                "Export your data or sign out of this device.",
                "Exportiere deine Daten oder melde dich auf diesem Gerät ab."
              )}
            >
              <Stack spacing={1.25}>
                <ActionRow
                  icon={<DownloadOutlinedIcon fontSize="small" />}
                  label={L("Export my data", "Meine Daten exportieren")}
                  description={L(
                    "Download everything we store about you as a file.",
                    "Lade alle über dich gespeicherten Daten als Datei herunter."
                  )}
                  onClick={exportUserData}
                />

                <ActionRow
                  icon={<LogoutOutlinedIcon fontSize="small" />}
                  label={L("Log out", "Abmelden")}
                  description={L(
                    "End your session on this device.",
                    "Beende deine Sitzung auf diesem Gerät."
                  )}
                  onClick={handleLogout}
                />
              </Stack>

              <Box
                sx={{
                  mt: 2,
                  borderRadius: 2.5,
                  border: `1px solid ${brand.border}`,
                  bgcolor: brand.surfaceMuted,
                  p: 1.75,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: 11,
                    letterSpacing: 1.2,
                    textTransform: "uppercase",
                    color: brand.accentStrong,
                    mb: 0.75,
                  }}
                >
                  {L("Permanent actions", "Endgültige Aktionen")}
                </Typography>

                <Typography variant="body2" sx={{ color: brand.textMuted, mb: 1.5, lineHeight: 1.55 }}>
                  {L(
                    "Deleting your profile removes your account and assessment history. This cannot be undone.",
                    "Beim Löschen deines Profils werden Konto und Assessment-Verlauf entfernt. Das lässt sich nicht rückgängig machen."
                  )}
                </Typography>

                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineOutlinedIcon />}
                  onClick={async () => {
                    const confirmDelete = window.confirm(
                      L(
                        "Are you sure you want to delete your account permanently?",
                        "Möchtest du dein Konto wirklich dauerhaft löschen?"
                      )
                    );
                    if (!confirmDelete) return;
                    await deleteAccount();
                  }}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2.5,
                    fontWeight: 900,
                    py: 1,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {L("Delete my profile", "Profil löschen")}
                </Button>
              </Box>
            </ProfileSection>

            {/* ---------------- support ---------------- */}
            <ProfileSection
              id="support"
              innerRef={registerSection("support")}
              icon={<HelpOutlineOutlinedIcon fontSize="small" />}
              title={L("Ask a question", "Frage stellen")}
              subtitle={L(
                "Send a message to the takeKONTROL team.",
                "Sende eine Nachricht an das takeKONTROL-Team."
              )}
              chip={L("Support", "Support")}
            >
              <Stack spacing={1.5}>
                {questionSuccess ? (
                  <Alert severity="success" onClose={() => setQuestionSuccess(null)} sx={{ borderRadius: 2.5 }}>
                    {questionSuccess}
                  </Alert>
                ) : null}
                {questionError ? (
                  <Alert severity="error" onClose={() => setQuestionError(null)} sx={{ borderRadius: 2.5 }}>
                    {questionError}
                  </Alert>
                ) : null}

                <TextField
                  value={questionText}
                  onChange={(e) => setQuestionText(e.target.value)}
                  placeholder={L("Type your question here…", "Gib hier deine Frage ein…")}
                  multiline
                  minRows={4}
                  fullWidth
                  disabled={loading || !employee?.id}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5, bgcolor: brand.surface } }}
                />

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.25}
                  alignItems={{ xs: "stretch", sm: "center" }}
                >
                  <Button
                    variant="contained"
                    onClick={sendQuestion}
                    disabled={!canSendQuestion}
                    startIcon={
                      sendingQuestion ? <CircularProgress size={18} color="inherit" /> : <EmailOutlinedIcon />
                    }
                    sx={{ ...btnPrimarySx, flexShrink: 0 }}
                  >
                    {sendingQuestion ? L("Sending…", "Wird gesendet…") : L("Send", "Senden")}
                  </Button>

                  <Typography variant="caption" sx={{ color: brand.textMuted, lineHeight: 1.5 }}>
                    {L(
                      "Please don't include sensitive personal data in your message.",
                      "Bitte keine sensiblen personenbezogenen Daten in die Nachricht schreiben."
                    )}
                  </Typography>
                </Stack>
              </Stack>
            </ProfileSection>
          </Stack>
        </Box>
      </Box>

      <ProfileEditDialog
        open={editDialogOpen}
        title={L("Edit profile", "Profil bearbeiten")}
        description={L("Update your personal profile details.", "Aktualisiere deine persönlichen Profildaten.")}
        form={identityForm}
        setForm={setIdentityForm}
        saving={saving}
        valid={identityFormValid}
        onClose={() => setEditDialogOpen(false)}
        onSave={saveProfileDetails}
        L={L}
      />

      <Dialog open={anonDialogOpen} onClose={() => setAnonDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{L("Switch to anonymous mode?", "In anonymen Modus wechseln?")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: brand.textMuted, lineHeight: 1.7 }}>
            {L(
              "This will remove your first name, last name, position and address from your employee profile. To use identifiable mode again, you will need to provide these details again.",
              "Dadurch werden Vorname, Nachname, Position und Adresse aus deinem Mitarbeiterprofil entfernt. Um den identifizierbaren Modus wieder zu nutzen, musst du diese Daten erneut angeben."
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnonDialogOpen(false)} disabled={saving}>{L("Cancel", "Abbrechen")}</Button>
          <Button variant="contained" onClick={enableAnonymousMode} disabled={saving} sx={btnPrimarySx}>
            {saving ? L("Saving…", "Speichern…") : L("Confirm anonymous mode", "Anonymen Modus bestätigen")}
          </Button>
        </DialogActions>
      </Dialog>

      <ProfileEditDialog
        open={identityDialogOpen}
        title={L("Provide your identity details", "Identitätsdaten angeben")}
        description={L(
          "To switch off anonymous mode, please provide your first name, last name, position and address.",
          "Um den anonymen Modus auszuschalten, gib bitte deinen Vornamen, Nachnamen, deine Position und Adresse an."
        )}
        form={identityForm}
        setForm={setIdentityForm}
        saving={saving}
        valid={identityFormValid}
        onClose={() => setIdentityDialogOpen(false)}
        onSave={saveProfileDetails}
        L={L}
      />
    </Box>
  );
}

/**
 * In-page navigation: a sticky rail beside the content on desktop, and a
 * horizontally scrollable strip above it on phones and small tablets.
 */
function ProfileNav(props: {
  items: { id: ProfileSectionId; label: string; icon: React.ReactNode }[];
  active: ProfileSectionId;
  onSelect: (id: ProfileSectionId) => void;
  modeCaption: string;
  modeLabel: string;
}) {
  const { items, active, onSelect, modeCaption, modeLabel } = props;

  return (
    <Paper
      elevation={0}
      component="nav"
      aria-label="Profile sections"
      sx={{
        borderRadius: 3,
        border: `1px solid ${brand.border}`,
        bgcolor: brand.surface,
        overflow: "hidden",
        // Stays reachable while the sections scroll past, on every screen size.
        position: "sticky",
        top: { xs: 0, md: 24 },
        zIndex: 2,
        mb: { xs: 1.5, md: 0 },
        boxShadow: { xs: "0 6px 18px rgba(17,24,39,0.06)", md: "none" },
      }}
    >
      <Box
        sx={{
          p: 1,
          display: "flex",
          flexDirection: { xs: "row", md: "column" },
          gap: 0.5,
          overflowX: { xs: "auto", md: "visible" },
          // Let the strip scroll on small screens without a visible scrollbar.
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {items.map((item) => {
          const selected = item.id === active;

          return (
            <ListItemButton
              key={item.id}
              selected={selected}
              disableRipple
              onClick={() => onSelect(item.id)}
              sx={{
                borderRadius: 2,
                py: 1,
                px: 1.25,
                flex: { xs: "0 0 auto", md: "initial" },
                whiteSpace: "nowrap",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: { xs: 8, md: 0 },
                  right: { xs: 8, md: "auto" },
                  top: { xs: "auto", md: 8 },
                  bottom: { xs: 2, md: 8 },
                  width: { md: 3 },
                  height: { xs: 3, md: "auto" },
                  borderRadius: 3,
                  bgcolor: selected ? brand.accent : "transparent",
                },
                "&.Mui-selected, &.Mui-selected:hover": { bgcolor: brand.surfaceSunken },
              }}
            >
              <ListItemIcon
                sx={{ minWidth: 32, color: selected ? brand.textPrimary : brand.textMuted }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    sx={{
                      fontWeight: selected ? 900 : 800,
                      fontSize: 14,
                      color: selected ? brand.textPrimary : brand.textSecondary,
                    }}
                  >
                    {item.label}
                  </Typography>
                }
              />
            </ListItemButton>
          );
        })}
      </Box>

      <Box
        sx={{
          display: { xs: "none", md: "block" },
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${brand.borderSoft}`,
          bgcolor: brand.surfaceMuted,
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 10,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            color: brand.textFaint,
          }}
        >
          {modeCaption}
        </Typography>
        <Typography sx={{ fontWeight: 900, fontSize: 13, color: brand.textPrimary, mt: 0.25 }}>
          {modeLabel}
        </Typography>
      </Box>
    </Paper>
  );
}

/** One titled, anchorable block of the profile. */
function ProfileSection(props: {
  id: ProfileSectionId;
  innerRef: (el: HTMLElement | null) => void;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  chip?: string;
  action?: React.ReactNode;
  disablePadding?: boolean;
  children: React.ReactNode;
}) {
  const { id, innerRef, icon, title, subtitle, chip, action, disablePadding, children } = props;

  return (
    <Paper
      elevation={0}
      component="section"
      ref={innerRef}
      id={`profile-${id}`}
      data-section={id}
      aria-label={title}
      sx={{
        borderRadius: 3,
        border: `1px solid ${brand.border}`,
        bgcolor: brand.surface,
        overflow: "hidden",
        scrollMarginTop: 24,
      }}
    >
      <Box sx={{ p: { xs: 1.75, md: 2 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1.25, sm: 1.5 }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: brand.page,
                border: `1px solid ${brand.border}`,
                display: "grid",
                placeItems: "center",
                color: brand.textPrimary,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography component="h2" sx={{ fontWeight: 900, color: brand.textPrimary }}>
                {title}
              </Typography>
              <Typography variant="body2" sx={{ color: brand.textMuted, lineHeight: 1.4 }}>
                {subtitle}
              </Typography>
            </Box>
          </Stack>

          {action ?? null}

          {chip ? (
            <Chip
              size="small"
              label={chip}
              variant="outlined"
              sx={{
                borderColor: brand.borderStrong,
                bgcolor: brand.surface,
                color: brand.textPrimary,
                fontWeight: 800,
                flexShrink: 0,
              }}
            />
          ) : null}
        </Stack>
      </Box>

      <Divider />

      <Box sx={disablePadding ? undefined : { p: { xs: 1.75, md: 2 } }}>{children}</Box>
    </Paper>
  );
}

function DetailRow(props: {
  icon: React.ReactNode;
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <Box
      sx={{
        py: 1.5,
        px: { xs: 1.75, md: 2 },
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        gap: { xs: 0.5, sm: 2 },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ color: brand.textMuted, display: "flex", flexShrink: 0 }}>{props.icon}</Box>
        <Typography sx={{ fontWeight: 800, color: brand.textSecondary, fontSize: 14 }}>
          {props.label}
        </Typography>
      </Stack>

      <Typography
        sx={{
          fontWeight: 800,
          color: props.muted ? brand.textFaint : brand.textPrimary,
          fontStyle: props.muted ? "italic" : "normal",
          textAlign: { xs: "left", sm: "right" },
          maxWidth: { xs: "100%", sm: "55%" },
          pl: { xs: 4.25, sm: 0 },
          overflowWrap: "anywhere",
        }}
      >
        {props.value}
      </Typography>
    </Box>
  );
}

/** A labelled action with room to explain what it does before it is clicked. */
function ActionRow(props: {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <ListItemButton
      onClick={props.onClick}
      disableRipple
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${brand.border}`,
        bgcolor: brand.surface,
        px: 1.75,
        py: 1.25,
        alignItems: "flex-start",
        "&:hover": { bgcolor: brand.surfaceMuted, borderColor: brand.borderStrong },
      }}
    >
      <ListItemIcon sx={{ minWidth: 34, color: brand.textPrimary, mt: 0.35 }}>
        {props.icon}
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography sx={{ fontWeight: 900, color: brand.textPrimary }}>{props.label}</Typography>
        }
        secondary={
          <Typography variant="body2" sx={{ color: brand.textMuted, lineHeight: 1.5 }}>
            {props.description}
          </Typography>
        }
      />
    </ListItemButton>
  );
}

function FactTile(props: { label: string; value: string }) {
  return (
    <Box
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${brand.border}`,
        bgcolor: brand.surfaceMuted,
        px: 1.75,
        py: 1.25,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontWeight: 900,
          fontSize: 10,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: brand.textFaint,
        }}
      >
        {props.label}
      </Typography>
      <Typography
        sx={{ color: brand.textPrimary, fontWeight: 900, mt: 0.25, overflowWrap: "anywhere" }}
      >
        {props.value}
      </Typography>
    </Box>
  );
}

function ProfileEditDialog(props: {
  open: boolean;
  title: string;
  description: string;
  form: {
    first_name: string;
    last_name: string;
    position: string;
    department: string;
    personal_address: string;
  };
  setForm: React.Dispatch<
    React.SetStateAction<{
      first_name: string;
      last_name: string;
      position: string;
      department: string;
      personal_address: string;
    }>
  >;
  saving: boolean;
  valid: boolean;
  onClose: () => void;
  onSave: () => void;
  L: (en: string, de: string) => string;
}) {
  const { open, title, description, form, setForm, saving, valid, onClose, onSave, L } = props;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography sx={{ color: "#6B7280", lineHeight: 1.7 }}>{description}</Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              label={L("First name", "Vorname")}
              value={form.first_name}
              onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
              fullWidth
            />

            <TextField
              label={L("Last name", "Nachname")}
              value={form.last_name}
              onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
              fullWidth
            />
          </Box>

          <TextField
            label={L("Position", "Position")}
            value={form.position}
            onChange={(e) => setForm((p) => ({ ...p, position: e.target.value }))}
            fullWidth
          />

          <TextField
            label={L("Department", "Abteilung")}
            value={form.department}
            onChange={(e) => setForm((p) => ({ ...p, department: e.target.value }))}
            fullWidth
          />

          <TextField
            label={L("Personal address", "Privatadresse")}
            value={form.personal_address}
            onChange={(e) => setForm((p) => ({ ...p, personal_address: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {L("Cancel", "Abbrechen")}
        </Button>

        <Button variant="contained" onClick={onSave} disabled={saving || !valid} sx={btnPrimarySx}>
          {saving ? L("Saving…", "Speichern…") : L("Save changes", "Änderungen speichern")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
